import { Camera, Check, Trash2, ZoomIn } from 'lucide-react';
import Cropper, { type Area } from 'react-easy-crop';
import { useCallback, useRef, useState } from 'react';
import { Dialog, Flex, IconButton, Slider, Spinner } from '@radix-ui/themes';
import { Box } from './Box';
import { Button } from './Button';
import { sizeMapper } from './RadixProps';
import { Text } from './Text';

/**
 * Crop a source image to the given pixel area and return a Blob.
 */
async function getCroppedBlob(imageSrc: string, croppedAreaPixels: Area): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Could not get canvas context'));
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );
      canvas.toBlob(
        blob => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas is empty'));
        },
        'image/jpeg',
        1.0
      );
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

type ImagePickerSize = 1 | 2 | 3 | 4;

const SIZES: Record<
  ImagePickerSize,
  {
    camera: number;
    text: '0.5' | '1' | '2' | '3';
    icon: number;
    button: 'sm' | 'md' | 'lg' | 'xl';
    spinner: '1' | '2' | '3';
  }
> = {
  1: {
    camera: 20,
    text: '0.5',
    icon: 14,
    button: 'sm',
    spinner: '1',
  },
  2: {
    camera: 24,
    text: '1',
    icon: 16,
    button: 'md',
    spinner: '2',
  },
  3: {
    camera: 32,
    text: '2',
    icon: 20,
    button: 'lg',
    spinner: '2',
  },
  4: {
    camera: 40,
    text: '3',
    icon: 24,
    button: 'xl',
    spinner: '3',
  },
};

export interface ImagePickerProps {
  /** Current download URL or undefined if empty */
  value: string | undefined;
  /** Slot label shown when empty, e.g. "Slot 1" */
  label: string;
  /** Called with the cropped+compressed Blob and a suggested filename */
  onUpload: (blob: Blob, filename: string) => Promise<void>;
  /** Called when the user removes the current photo */
  onRemove: () => void | Promise<void>;
  /** Disable interactions (e.g. max photos reached or a sibling is uploading) */
  disabled?: boolean;
  /** Visual size tier (1-4); 2 is the default */
  size?: ImagePickerSize;
}

function TileContent({
  value,
  uploading,
  label,
  size,
}: {
  value: string | undefined;
  uploading: boolean;
  label: string;
  size: ImagePickerSize;
}) {
  const s = SIZES[size];

  if (value) {
    return <img src={value} alt={label} draggable={false} referrerPolicy="no-referrer" />;
  }
  if (uploading) {
    return <Spinner size={s.spinner} />;
  }
  return (
    <Flex direction="column" align="center" justify="center" gap="1">
      <Camera size={s.camera} />
      <Text as="span" size={s.text}>
        {label}
      </Text>
    </Flex>
  );
}

/**
 * A single photo slot that handles file selection, in-dialog crop, compression
 * and upload. All heavy work (compression + upload) is delegated to the
 * `onUpload` callback so the component stays UI-only.
 */
export function ImagePicker({
  value,
  label,
  onUpload,
  onRemove,
  disabled = false,
  size = 2,
}: ImagePickerProps) {
  const s = SIZES[size];
  const inputRef = useRef<HTMLInputElement>(null);

  // Raw data-URL of the selected file (before crop)
  const [rawSrc, setRawSrc] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setDialogOpen(true);
    };
    reader.readAsDataURL(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!rawSrc || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(rawSrc, croppedAreaPixels);
      const filename = `photo_${Date.now()}.webp`;
      setDialogOpen(false);
      setRawSrc(null);
      await onUpload(blob, filename);
    } finally {
      setUploading(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!uploading) {
      setDialogOpen(open);
      if (!open) setRawSrc(null);
    }
  };

  return (
    <Box className="image-picker">
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={disabled || !!value}
      />

      {/* Slot tile */}
      <Box position="relative">
        <Button
          type="button"
          disabled={disabled || !!value}
          onClick={() => !value && inputRef.current?.click()}
          aria-label={value ? label : `Upload ${label}`}
          variant="outline"
          color="gray"
          size={s.button}
          className="image-picker-tile"
        >
          <TileContent value={value} uploading={uploading} label={label} size={size} />
        </Button>

        {/* Remove button shown over filled slot */}
        {value && (
          <IconButton
            type="button"
            onClick={onRemove}
            onPointerDown={e => e.stopPropagation()}
            disabled={disabled}
            variant="outline"
            highContrast
            color="gray"
            aria-label={`Remove ${label}`}
            size={sizeMapper[s.button]}
            radius="full"
            className="image-picker-remove"
          >
            <Trash2 size={s.icon} />
          </IconButton>
        )}
      </Box>

      {/* Crop dialog */}
      <Dialog.Root open={dialogOpen} onOpenChange={handleDialogClose}>
        <Dialog.Content className="image-picker" aria-describedby={undefined}>
          <Dialog.Title className="sr-only">Crop photo</Dialog.Title>

          {/* Crop area */}
          <Box className="image-picker-cropper" position="relative">
            {rawSrc && (
              <Cropper
                image={rawSrc}
                crop={crop}
                zoom={zoom}
                aspect={2 / 3}
                objectFit="cover"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={false}
              />
            )}
          </Box>

          {/* Zoom slider + actions */}
          <Flex direction="column" gap="3" p="4">
            <Flex align="center" gap="3">
              <ZoomIn className="icon" />
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.05}
                onValueChange={([val]) => setZoom(val)}
                aria-label="Zoom"
              />
            </Flex>

            <Flex gap="3" mx="auto">
              <Button
                type="button"
                onClick={() => handleDialogClose(false)}
                disabled={uploading}
                variant="outline"
                color="gray"
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleConfirm}
                disabled={uploading || !croppedAreaPixels}
                variant="solid"
              >
                {uploading ? (
                  <>
                    <Spinner size="2" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Check />
                    Use Photo
                  </>
                )}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}
